import classNames from "classnames/bind";
import { get } from "es-toolkit/compat";
import React, { useCallback } from "react";
import {
  RadioSelectionCard,
  RadioSelectionCardOptionType,
} from "../Cards/RadioSelectionCard";
import styles from "./Radio.module.scss";
import RadioOption from "./RadioOption";
import type { RadioProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Radio buttons allow a user to make a single selection out of multiple choices
 */
const Radio = ({
  name,
  options,
  value,
  onChange: onChangeCallback,
  valueKey,
  labelKey,
  isDisabled,
  isLoading,
  shouldShowRadioIcon = true,
  shouldUseRadioSelectionCard = false,
  className,
  optionClassName,
  style,
}: RadioProps) => {
  const getLabel = useCallback(
    (option: Record<string, any> | string) => {
      if (typeof option === "string") {
        return option;
      } else {
        return get(option, labelKey ?? "");
      }
    },
    [labelKey]
  );

  const getValue = useCallback(
    (option: Record<string, any> | string) => {
      if (typeof option === "string") {
        return option;
      } else {
        return get(option, valueKey ?? "");
      }
    },
    [valueKey]
  );

  const onChange = (newValue: string) => {
    if (!isDisabled) {
      onChangeCallback?.(newValue);
    }
  };

  const handleKeyDown = (
    evt: React.KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    if (evt.code === "Enter") {
      onChange((options as RadioSelectionCardOptionType[])[index].value);
    }
  };

  return (
    <div
      className={cx("radioContainer", className)}
      style={style}
    >
      {options?.map((option, index) => {
        return shouldUseRadioSelectionCard ? (
          <RadioSelectionCard
            key={`${name}-${(option as RadioSelectionCardOptionType)?.value}`}
            className={optionClassName}
            name={name}
            index={index}
            option={option as RadioSelectionCardOptionType}
            onChange={onChange}
            onKeyDown={(evt: React.KeyboardEvent<HTMLElement>) => {
              handleKeyDown(evt, index);
            }}
            value={value}
            isLoading={isLoading}
            isDisabled={isDisabled}
            shouldShowRadioIcon={shouldShowRadioIcon}
          />
        ) : (
          <RadioOption
            key={`${name}-${getValue(option)}`}
            name={name}
            index={index}
            option={option}
            getValue={getValue}
            getLabel={getLabel}
            onChange={onChange}
            value={value}
            isLoading={isLoading}
            isDisabled={isDisabled}
          />
        );
      })}
    </div>
  );
};

export default Radio;
